import { apiClient } from "@/lib/api";
import type { Changed } from "@/components/table/changedColumns";

// The pallet account (docs/scantafel.md §5, services/api modules/palletAccount): per relation and kind of value — Euro,
// H1, H3 — a balance made of mutations. Every approved pallet check books on the principal, and on the charter that
// drove; by hand a pallet slip, a correction, pallets bought or charged, an opening balance. A quantity counts from
// Willems: positive is what the relation owes Willems.

export const ACCOUNT_KINDS = ["euro", "h1", "h3"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
export type AccountRole = "principal" | "charter";
export const MANUAL_REASONS = ["pallet_slip", "correction", "purchased", "charged", "opening_balance"] as const;
export type ManualReason = (typeof MANUAL_REASONS)[number];

export type Balances = Record<AccountKind, number>;

export interface Agreement {
  exchanges: boolean;
  kinds: AccountKind[];
  purchasesInbound: boolean;
  note: string | null;
  /** Whether the relation set one; without, it exchanges all three. */
  set: boolean;
}

export interface AccountRow extends Changed {
  party: { id: string; code: string; name: string };
  role: AccountRole;
  balances: Balances;
  lastAt: string | null;
  mutations: number;
  agreement: Agreement;
}

export interface Mutation extends Changed {
  id: string;
  role: AccountRole;
  kind: AccountKind;
  quantity: number;
  balanceAfter: number;
  source: "check" | "manual";
  reason: string;
  occurredAt: string;
  note: string | null;
  /** A stop that left the relation owing Willems: to charge or keep. */
  shortage: boolean;
  settlement: "charge" | "keep" | null;
  settledAt: string | null;
  settledBy: string | null;
  attachment: { name: string; size: number | null } | null;
  stop: { activityType: string; place: string } | null;
  consignment: { id: string; number: string; orderId: string; orderNumber: string } | null;
  trip: { id: string; tripNumber: string } | null;
  document: { id: string; documentType: string | null } | null;
  createdBy: string | null;
  createdAt: string;
}

export interface AccountDetail {
  party: { id: string; code: string; name: string; isCharter: boolean };
  agreement: Agreement;
  balances: Record<AccountRole, Balances>;
  mutations: Mutation[];
}

export interface PurchaseProposal {
  party: { id: string; code: string; name: string };
  month: string;
  inbound: number;
  notReturned: number;
  proposed: number;
  booked: { quantity: number; at: string; note: string | null } | null;
}

export const KIND_LABEL_KEYS: Record<AccountKind, string> = { euro: "pallets.kind.euro", h1: "pallets.kind.h1", h3: "pallets.kind.h3" };

export const palletAccountKeys = {
  all: ["pallet-accounts"] as const,
  list: ["pallet-accounts", "list"] as const,
  detail: (partyId: string) => ["pallet-accounts", "detail", partyId] as const,
  purchases: (month: string) => ["pallet-accounts", "purchases", month] as const,
};

export function listAccounts(): Promise<{ items: AccountRow[] }> {
  return apiClient.get("/api/v1/pallet-accounts");
}

export function getAccount(partyId: string): Promise<AccountDetail> {
  return apiClient.get(`/api/v1/pallet-accounts/${partyId}`);
}

export function addMutation(
  partyId: string,
  body: { role: AccountRole; kind: AccountKind; quantity: number; reason: ManualReason; note?: string; occurredOn?: string },
): Promise<{ mutation: Mutation }> {
  return apiClient.post(`/api/v1/pallet-accounts/${partyId}/mutations`, body);
}

/** The pallet slip of a mutation by hand: the file itself as the body. */
export function attachToMutation(id: string, file: File): Promise<{ mutation: Mutation }> {
  return apiClient.postRaw(`/api/v1/pallet-mutations/${id}/attachment?filename=${encodeURIComponent(file.name)}`, file, file.type || "application/octet-stream");
}

export function attachmentUrl(id: string): string {
  return `/api/v1/pallet-mutations/${id}/attachment`;
}

export function setSettlement(id: string, settlement: "charge" | "keep" | null): Promise<{ mutation: Mutation }> {
  return apiClient.put(`/api/v1/pallet-mutations/${id}/settlement`, { settlement });
}

export function setAgreement(partyId: string, body: { exchanges: boolean; kinds: AccountKind[]; purchasesInbound: boolean; note?: string }): Promise<{ agreement: Agreement }> {
  return apiClient.put(`/api/v1/pallet-accounts/${partyId}/agreement`, body);
}

export function listPurchases(month: string): Promise<{ items: PurchaseProposal[] }> {
  return apiClient.get("/api/v1/pallet-accounts/purchases", { month });
}

export function bookPurchase(body: { partyId: string; month: string; inbound: number; notReturned: number; note?: string }): Promise<{ mutation: Mutation }> {
  return apiClient.post("/api/v1/pallet-accounts/purchases", body);
}

/** "+3", "−21", "0": from Willems, with the sign. */
export function signed(value: number): string {
  if (value === 0) return "0";
  return `${value > 0 ? "+" : "−"}${Math.abs(value).toLocaleString("nl-NL")}`;
}
